import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { FileText } from 'lucide-react';
import { useAppContext } from '@/contexts/AppContext';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';

interface JobByJobSummaryProps {
  driverName: string;
  startDate: string;
  endDate: string;
}

interface JobData {
  id: string;
  bookingId: string;
  date: string;
  passengerName: string;
  pickupAddress: string;
  paymentType: string;
  paidAmount: number;
  vehicleId: string;
  puDate?: string;
}

const JobByJobSummary: React.FC<JobByJobSummaryProps> = ({ driverName, startDate, endDate }) => {
  const { drivers } = useAppContext();
  const [jobs, setJobs] = useState<JobData[]>([]);
  const [loading, setLoading] = useState(true);

  const currentDriver = drivers.find(d => d.driver_name === driverName);

  useEffect(() => {
    loadJobData();
  }, [driverName, startDate, endDate]);

  const loadJobData = async () => {
    if (!currentDriver) {
      setJobs([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const vehicleId = currentDriver.vehicle_id || currentDriver.driver_name;

      const { data: manualUploads, error: manualError } = await supabase
        .from('DriverManualUploads')
        .select('*')
        .eq('VehicleID', vehicleId)
        .gte('PaymentDate', startDate)
        .lte('PaymentDate', endDate)
        .order('PaymentDate', { ascending: true });

      if (manualError) console.error('Error loading manual uploads:', manualError);

      const { data: driverJobs, error: driverError } = await supabase
        .from('driver_jobs')
        .select('*, taxicaller_data (pu_date)')
        .eq('driver_id', currentDriver.driver_name)
        .gte('job_timestamp', startDate)
        .lte('job_timestamp', endDate + 'T23:59:59')
        .order('job_timestamp', { ascending: true });

      if (driverError) console.error('Error loading driver jobs:', driverError);

      const allJobs: JobData[] = [];

      if (manualUploads) {
        manualUploads.forEach(upload => {
          allJobs.push({
            id: upload.BookingID,
            bookingId: upload.BookingID,
            date: upload.PaymentDate,
            passengerName: upload.PassengerName || 'N/A',
            pickupAddress: upload.PickupAddress || 'N/A',
            paymentType: upload.PaymentType || 'N/A',
            paidAmount: parseFloat(upload.PaidAmount || 0),
            vehicleId: upload.VehicleID || 'N/A',
            puDate: upload.PaymentDate
          });
        });
      }

      if (driverJobs) {
        driverJobs.forEach(job => {
          if (!allJobs.find(j => j.bookingId === job.id)) {
            allJobs.push({
              id: job.id,
              bookingId: job.id,
              date: new Date(job.job_timestamp).toISOString().split('T')[0],
              passengerName: job.customer_name || 'N/A',
              pickupAddress: job.pickup_address || 'N/A',
              paymentType: 'Mixed',
              paidAmount: parseFloat(job.total_fare || 0),
              vehicleId,
              puDate: job.taxicaller_data?.pu_date || job.job_timestamp
            });
          }
        });
      }

      allJobs.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setJobs(allJobs);

    } catch (error) {
      console.error('Error loading job data:', error);
      toast({
        title: 'Error loading jobs',
        description: 'Failed to load job data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateJobByJobExcel = () => {
    const csvContent = [
      ['Job by Job Report'],
      [`Driver: ${driverName}`],
      [`Period: ${formatDate(startDate)} - ${formatDate(endDate)}`],
      [''],
      ['BookingID', 'Passenger Name', 'Pickup Address', 'Payment Type', 'Paid Amount', 'Payment Date', 'Pickup Date', 'Vehicle ID'],
      ...jobs.map(job => [
        job.bookingId,
        job.passengerName,
        job.pickupAddress,
        job.paymentType,
        `$${job.paidAmount.toFixed(2)}`,
        formatDate(job.date),
        job.puDate ? formatDate(job.puDate) : 'N/A',
        job.vehicleId
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `job-by-job-${driverName.replace(/\s+/g, '-')}-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Download Started',
      description: 'Job by job report has been downloaded.'
    });
  };

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-6">
          <div className="text-center">Loading job data...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl text-blue-800">
          Job by Job Summary - {driverName}
        </CardTitle>
        <p className="text-sm text-gray-600">
          Period: {formatDate(startDate)} - {formatDate(endDate)}
        </p>
        <div className="flex gap-3 mt-4">
          <Button onClick={generateJobByJobExcel} className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Download Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {jobs.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>BookingID</TableHead>
                  <TableHead>Passenger Name</TableHead>
                  <TableHead>Pickup Address</TableHead>
                  <TableHead>Payment Type</TableHead>
                  <TableHead className="text-right">Paid Amount</TableHead>
                  <TableHead>Payment Date</TableHead>
                  <TableHead>Pickup Date</TableHead>
                  <TableHead>Vehicle ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-medium">{job.bookingId}</TableCell>
                    <TableCell>{job.passengerName}</TableCell>
                    <TableCell className="max-w-xs truncate" title={job.pickupAddress}>
                      {job.pickupAddress}
                    </TableCell>
                    <TableCell>{job.paymentType}</TableCell>
                    <TableCell className="text-right font-semibold">
                      ${job.paidAmount.toFixed(2)}
                    </TableCell>
                    <TableCell>{formatDate(job.date)}</TableCell>
                    <TableCell>{job.puDate ? formatDate(job.puDate) : 'N/A'}</TableCell>
                    <TableCell>{job.vehicleId}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No job data found for this period
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobByJobSummary;
